import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronDown } from 'lucide-react';
import './RowsPerPageSelect.css';

// "Rows per page" picker for a list pager. A popover rather than a native
// <select> so the open list can be styled to match the app.
export default function RowsPerPageSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(true);
  const ref = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handleMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  function toggle() {
    if (!open && ref.current) {
      // The pager sits at the bottom of the list, so open upward unless
      // there's more room below.
      const rect = ref.current.getBoundingClientRect();
      setDropUp(rect.top > window.innerHeight - rect.bottom);
    }
    setOpen((v) => !v);
  }

  function choose(size) {
    setOpen(false);
    if (size !== value) onChange(size);
  }

  return (
    <div className="rows-per-page" ref={ref}>
      <span className="rows-per-page-label">Rows per page:</span>
      <button
        ref={toggleRef}
        type="button"
        className="rows-per-page-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Rows per page: ${value}`}
        onClick={toggle}
      >
        {value}
        <ChevronDown size={16} />
      </button>

      {open && (
        <div
          className={`rows-per-page-menu ${dropUp ? 'drop-up' : 'drop-down'}`}
          role="listbox"
          aria-label="Rows per page"
        >
          {options.map((size) => (
            <button
              key={size}
              type="button"
              role="option"
              aria-selected={size === value}
              className={`rows-per-page-option${size === value ? ' selected' : ''}`}
              onClick={() => choose(size)}
            >
              {size}
              {size === value && <Check size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

RowsPerPageSelect.propTypes = {
  value: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};
